import { useEffect, useRef, useState } from "react";
import { AlertCircle, Bell, Check, Clock3, Save } from "lucide-react";

import { supabase } from "../lib/supabase";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { Switch } from "./ui/switch";

interface EscalationConfigurationProps {
  teamId: string;
  userRole: "owner" | "admin" | "member" | "manager";
}

interface EscalationConfig {
  reminder_enabled: boolean;
  reminder_lead_minutes: number;
  auto_escalate_enabled: boolean;
  escalate_after_misses: number;
}

interface StandupSchedule {
  digest_time?: string;
  timezone?: string;
}

const DEFAULT_CONFIG: EscalationConfig = {
  reminder_enabled: true,
  reminder_lead_minutes: 30,
  auto_escalate_enabled: true,
  escalate_after_misses: 2,
};

const REMINDER_OPTIONS = [15, 30, 60, 120];
const DEFAULT_TIMEZONE = "UTC";

function formatDeadline(time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return time;
  return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function EscalationConfiguration({
  teamId,
  userRole,
}: EscalationConfigurationProps) {
  const [config, setConfig] = useState<EscalationConfig>(DEFAULT_CONFIG);
  const [schedule, setSchedule] = useState<StandupSchedule>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const loadedConfigRef = useRef<EscalationConfig>(DEFAULT_CONFIG);
  const isAdmin = userRole === "admin" || userRole === "owner";
  const hasChanges = JSON.stringify(config) !== JSON.stringify(loadedConfigRef.current);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data, error: loadError } = await (supabase as any)
          .from("teams")
          .select("escalation_config, standup_config")
          .eq("id", teamId)
          .single();
        if (loadError) throw loadError;
        if (!cancelled) {
          const loadedConfig = {
            ...DEFAULT_CONFIG,
            ...(data?.escalation_config ?? {}),
          };
          loadedConfigRef.current = loadedConfig;
          setConfig(loadedConfig);
          setSchedule((data?.standup_config as StandupSchedule | null) ?? {});
        }
      } catch (loadError: any) {
        if (!cancelled)
          setError(loadError.message || "Failed to load escalation settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const update = <K extends keyof EscalationConfig>(
    key: K,
    value: EscalationConfig[K],
  ) => {
    setConfig((current) => ({ ...current, [key]: value }));
    setSuccess("");
  };

  const save = async () => {
    if (!isAdmin) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data: currentTeam, error: loadError } = await (supabase as any)
        .from("teams")
        .select("escalation_config")
        .eq("id", teamId)
        .single();
      if (loadError) throw loadError;

      const { error: saveError } = await (supabase as any)
        .from("teams")
        .update({
          escalation_config: {
            ...(currentTeam?.escalation_config ?? {}),
            ...config,
          },
        })
        .eq("id", teamId);
      if (saveError) throw saveError;
      loadedConfigRef.current = config;
      setSuccess("Reminder and follow-up settings saved.");
    } catch (saveError: any) {
      setError(saveError.message || "Failed to save escalation settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3 bg-background p-6">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-4 w-96 max-w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    <div className="bg-background">
      <div className="border-b border-border p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-foreground">
          Reminders & follow-up
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Decide when people are reminded and when managers need to follow up.
        </p>
        <div className="mt-4 grid gap-3 border-t border-border pt-4 text-sm sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Current team deadline
            </p>
            <p className="mt-1 font-medium text-foreground">
              {schedule.digest_time ? formatDeadline(schedule.digest_time) : "Not configured"}{" "}
              {schedule.digest_time && (
                <span className="font-normal text-muted-foreground">
                  ({schedule.timezone ?? DEFAULT_TIMEZONE})
                </span>
              )}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Based on the team standup schedule.
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Notification recipients
            </p>
            <p className="mt-1 font-medium text-foreground">Person, then team manager</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Team admins are notified if no manager is assigned.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="flex items-center gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive sm:px-6"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div
          role="status"
          className="flex items-center gap-2 border-b border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground sm:px-6"
        >
          <Check className="h-4 w-4 shrink-0" />
          {success}
        </div>
      )}

      <div className="border-b border-border bg-muted/30 px-4 py-4 sm:px-6">
        <p className="text-sm font-medium text-foreground">What will happen</p>
        <div className="mt-3 grid gap-3 text-xs text-muted-foreground sm:grid-cols-3">
          <div className="flex gap-2">
            <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              {config.reminder_enabled
                ? `Before the deadline, remind people ${config.reminder_lead_minutes} minutes in advance.`
                : "Reminders are currently turned off."}
            </span>
          </div>
          <div className="flex gap-2">
            <Bell className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>After the deadline, treat the missing standup as missed.</span>
          </div>
          <div className="flex gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              {config.auto_escalate_enabled
                ? `After ${config.escalate_after_misses} consecutive missed standup${config.escalate_after_misses === 1 ? "" : "s"}, notify the team manager.`
                : "Manager alerts are currently turned off."}
            </span>
          </div>
        </div>
      </div>

      <div className="divide-y divide-border">
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="flex items-start gap-3">
            <div>
              <div className="text-sm font-medium text-foreground">
                Remind people before the deadline
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Nudge anyone who has not submitted their standup yet.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:shrink-0">
            <Select
              value={String(config.reminder_lead_minutes)}
              onValueChange={(value) =>
                update("reminder_lead_minutes", Number(value))
              }
              disabled={!isAdmin || !config.reminder_enabled}
            >
              <SelectTrigger aria-label="Reminder lead time" className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REMINDER_OPTIONS.map((minutes) => (
                  <SelectItem key={minutes} value={String(minutes)}>
                    {minutes < 60
                      ? `${minutes} minutes before`
                      : `${minutes / 60} hour${minutes === 60 ? "" : "s"} before`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Switch
              checked={config.reminder_enabled}
              onCheckedChange={(checked) => update("reminder_enabled", checked)}
              disabled={!isAdmin}
              aria-label="Enable reminder"
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <div className="text-sm font-medium text-foreground">
              Alert managers after repeated misses
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Notify the team manager after consecutive missed standups.
            </p>
          </div>
          <div className="flex items-center gap-3 sm:shrink-0">
            <Select
              value={String(config.escalate_after_misses)}
              onValueChange={(value) =>
                update("escalate_after_misses", Number(value))
              }
              disabled={!isAdmin || !config.auto_escalate_enabled}
            >
              <SelectTrigger aria-label="Consecutive misses" className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((count) => (
                  <SelectItem key={count} value={String(count)}>
                    {count} missed standup{count === 1 ? "" : "s"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Switch
              checked={config.auto_escalate_enabled}
              onCheckedChange={(checked) =>
                update("auto_escalate_enabled", checked)
              }
              disabled={!isAdmin}
              aria-label="Enable automatic escalation"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 p-4 sm:p-6">
          <div>
            <div className="text-sm font-medium text-foreground">Notifications</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Manager alerts are delivered through Slack.
            </p>
          </div>
          <span className="rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
            Slack notifications
          </span>
        </div>
      </div>

      <div className="border-t border-border bg-muted/20 px-4 py-4 sm:px-6">
        <p className="text-sm font-medium text-foreground">Team-specific settings</p>
        <p className="mt-1 text-xs text-muted-foreground">
          These are the team defaults. A team can have its own reminder timing or
          missed-standup threshold when a team manager or team admin sets an override.
        </p>
      </div>

      <div className="flex flex-col gap-3 border-t border-border p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        {!isAdmin && (
          <p className="text-sm text-muted-foreground">
            Only team admins can modify escalation settings.
          </p>
        )}
        {isAdmin && hasChanges && (
          <p className="text-sm text-muted-foreground">Unsaved changes</p>
        )}
        <Button
          onClick={() => void save()}
          disabled={!isAdmin || saving || !hasChanges}
          className="ml-auto min-w-32"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
